/**
 * CFS basket behaviour for the cart drawer and cart page (build spec §7).
 *
 * Zapiet (Pickup + Delivery) still owns delivery method, date and time; this file only reacts:
 * - Mount: <div id="storePickupApp"> lives in a [data-skip-node-update] wrapper so drawer
 *   re-renders keep it. If a re-render ever leaves an empty mount, `zapiet:start` is dispatched
 *   again (Zapiet's documented pattern for custom cart drawers).
 * - Selected method: read with `Zapiet.getSelectedCheckoutMethod()` and followed through
 *   `ZapietEvent.listen('selected_method', …)`. In this store Zapiet offers "shipping" (all
 *   delivery, local 1-hour and UK, priced at checkout) and "pickup". It also writes the cart
 *   attribute `Checkout-Method` and a `_ZapietId` line property (M=S shipping, M=P pickup).
 * - Checkout availability: Zapiet fires `checkoutDisabled` / `checkoutEnabled` (e.g. until a
 *   pickup date and time are chosen, or below the pickup minimum) but cannot find Horizon's
 *   button, so the Checkout button here follows those events.
 * Cart page: Horizon's section morph would empty Zapiet's widget, so the page basket re-renders
 * itself after cart changes: it detaches the live Zapiet node, morphs the rest, and puts the same
 * node back (the drawer instead keeps its mount outside Horizon's re-rendered area).
 * Delivery: no postcode step (owner decision, 4 Oct 2026). Local delivery is decided by Zapiet
 * (distance) and UK rates by Parcel at checkout; the basket shows the £40 local bar, the £65 UK
 * note and the total before delivery.
 * Multibuys: Bundler's own discounted prices are shown on lines, as a savings row and in the total.
 */
import { setVariantQuantity, CART_STATE_EVENT } from '@theme/cfs-cart';
import { morph } from '@theme/morph';


/** Zapiet state shared by every basket on the page (drawer and cart page). */
const zapiet = {
  /** Zapiet has rendered its widget at least once on this page (it starts itself on load). */
  rendered: false,
  /** @type {string | null} */
  method: null,
  /** Checkout blocked by Zapiet (e.g. no pickup slot yet). */
  blocked: false,
};

function readSelectedMethod() {
  try {
    const method = window.Zapiet?.getSelectedCheckoutMethod?.();
    return typeof method === 'string' && method ? method : null;
  } catch {
    return null;
  }
}

/**
 * A line's warning ("Only 7 available") by variant id, kept for a few seconds: the basket re-renders
 * from the server right after a refused change, which would wipe it straight away (owner).
 * @type {Map<string, { text: string, until: number }>}
 */
const lineWarnings = new Map();
const LINE_WARNING_MS = 6000;

/*
 * Undo after a removal (owner: mis-taps on Remove): "… removed · Undo" for a few seconds, in the
 * removed line's place in the list (CfsBasket #placeUndo), or in the empty drawer / empty basket
 * page when it was the last item (a <cfs-basket-undo> in their markup). Kept in sessionStorage so it survives the basket page's reload when the last
 * item goes. Undo adds the variant back at its old quantity (a Bundler bundle line comes back as
 * an ordinary line; Bundler re-applies the multibuy at checkout if the set is complete).
 */
const UNDO_KEY = 'cfs:undo';
const UNDO_MS = 10000;
const UNDO_EVENT = 'cfs:undo-change';

/** @returns {{ variantId: string, quantity: number, title: string, index: number, until: number } | null} */
function readUndo() {
  try {
    const undo = JSON.parse(sessionStorage.getItem(UNDO_KEY) ?? 'null');
    return undo && undo.until > Date.now() ? undo : null;
  } catch {
    return null;
  }
}

/** @param {{ variantId: string, quantity: number, title: string, index: number } | null} removed */
function setUndo(removed) {
  try {
    if (removed) sessionStorage.setItem(UNDO_KEY, JSON.stringify({ ...removed, until: Date.now() + UNDO_MS }));
    else sessionStorage.removeItem(UNDO_KEY);
  } catch {
    // Private mode: no undo, the removal itself still works
  }
  document.dispatchEvent(new CustomEvent(UNDO_EVENT));
}

/*
 * After Undo the item comes back at the top of the basket (Shopify lists the newest line first and
 * lines can't be reordered), so the basket scrolls to it and its background fades from light green
 * (owner). Kept in sessionStorage for the empty basket page, which reloads.
 */
const FLASH_KEY = 'cfs:flash';
/** @param {string} variantId */
function flashLine(variantId) {
  try {
    sessionStorage.setItem(FLASH_KEY, JSON.stringify({ variantId, until: Date.now() + 8000 }));
  } catch {
    // Private mode: no flash
  }
}

/** @param {ParentNode} root */
function showFlash(root) {
  /** @type {{ variantId: string, until: number } | null} */
  let flash = null;
  try {
    flash = JSON.parse(sessionStorage.getItem(FLASH_KEY) ?? 'null');
  } catch {
    return;
  }
  if (!flash) return;
  if (flash.until < Date.now()) {
    sessionStorage.removeItem(FLASH_KEY);
    return;
  }
  const line = /** @type {HTMLElement | null} */ (root.querySelector(`.cfs-line[data-variant-id="${flash.variantId}"]`));
  if (!line) return; // not re-rendered yet: the next pass finds it
  sessionStorage.removeItem(FLASH_KEY);
  const reduce = matchMedia('(prefers-reduced-motion: reduce)').matches;
  const behavior = reduce ? 'auto' : 'smooth';
  // The item is now first in the list: the drawer goes right back to its top; the basket page puts
  // the line just under the compact header (not merely "in view", owner).
  let scroller = line.parentElement;
  while (scroller && !/(auto|scroll)/.test(getComputedStyle(scroller).overflowY)) scroller = scroller.parentElement;
  if (scroller && line.closest('#cart-drawer')) scroller.scrollTo({ top: 0, behavior });
  else window.scrollTo({ top: Math.max(0, line.getBoundingClientRect().top + window.scrollY - 90), behavior });
  line.classList.remove('cfs-line--back');
  void line.offsetWidth; // restart the animation
  line.classList.add('cfs-line--back');
  line.addEventListener('animationend', () => line.classList.remove('cfs-line--back'), { once: true });
}

class CfsBasketUndo extends HTMLElement {
  #timer = 0;

  connectedCallback() {
    document.addEventListener(UNDO_EVENT, this.#render);
    this.addEventListener('click', this.#onClick);
    this.#render();
  }

  /** After a basket re-render (the morph empties it to match the server's markup). */
  refresh() {
    if (!this.firstElementChild) delete this.dataset.key;
    this.#render();
  }

  disconnectedCallback() {
    document.removeEventListener(UNDO_EVENT, this.#render);
    window.clearTimeout(this.#timer);
  }

  #render = () => {
    window.clearTimeout(this.#timer);
    const undo = readUndo();
    const key = undo ? `${undo.variantId}:${undo.until}` : '';
    // Always re-armed: a re-render calls this again with the same undo, and the row must still
    // disappear when it runs out (it used to stay, with a button that no longer did anything).
    if (undo) this.#timer = window.setTimeout(this.#render, undo.until - Date.now() + 20);
    if (this.dataset.key === key) return;
    this.dataset.key = key;
    if (!undo) {
      this.replaceChildren();
      return;
    }
    const row = document.createElement('p');
    row.className = 'cfs-undo';
    row.setAttribute('role', 'status');
    const text = document.createElement('span');
    text.className = 'cfs-undo__text';
    text.textContent = `${undo.title} removed`;
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'cfs-undo__button';
    button.textContent = 'Undo';
    button.setAttribute('aria-label', `Undo: put ${undo.title} back in your basket`);
    row.append(text, button);
    this.replaceChildren(row);
  };

  /** @param {MouseEvent} event */
  #onClick = async (event) => {
    const button = /** @type {HTMLElement} */ (event.target).closest?.('.cfs-undo__button');
    const undo = readUndo();
    if (!button || !undo || button.hasAttribute('aria-busy')) return;
    button.setAttribute('aria-busy', 'true');
    try {
      flashLine(undo.variantId);
      await setVariantQuantity({ variantId: undo.variantId, quantity: undo.quantity, source: this });
      setUndo(null);
      // The empty basket page has no basket to update: load it again with the item back
      if (!document.querySelector('.cfs-basket--page') && this.closest('.cfs-cart-page')) window.location.reload();
    } catch (error) {
      button.removeAttribute('aria-busy');
      const text = this.querySelector('.cfs-undo__text');
      if (text) text.textContent = /** @type {Error} */ (error)?.message || 'Could not put it back';
    }
  };
}
if (!customElements.get('cfs-basket-undo')) customElements.define('cfs-basket-undo', CfsBasketUndo);

const listeners = new Set();
const notify = () => listeners.forEach((fn) => fn());

/*
 * CFS: write only when something changes. Bundler watches #cart-drawer (attributes + childList)
 * and recalculates, then re-fetches its cart, on every mutation it sees; Magical Messages starts a
 * re-render on every childList mutation in <body>. Rewriting the same text or attribute still
 * counts as a mutation, which kept both apps busy (and Bundler polling) while the page sat idle.
 */
/** @param {Element | null} el @param {string} text */
const setText = (el, text) => {
  if (el && el.textContent !== text) el.textContent = text;
};
/** @param {Element | null} el @param {string} html */
const setHtml = (el, html) => {
  if (el && el.innerHTML !== html) el.innerHTML = html;
};
/** @param {HTMLElement | null} el @param {boolean} hidden */
const setHidden = (el, hidden) => {
  if (el && el.hidden !== hidden) el.hidden = hidden;
};
/** @param {Element | null} el @param {string} name @param {string} value */
const setAttr = (el, name, value) => {
  if (el && el.getAttribute(name) !== value) el.setAttribute(name, value);
};

/*
 * Bundler multibuys. Bundler applies bundle prices at checkout, but works them out in the
 * browser and reports them with `bundler:total_cart_values` (after every cart change). The basket
 * only displays Bundler's own figures (line prices struck through, "Multibuy savings", total);
 * it never calculates a bundle price itself (spec §13). Figures that don't match the lines on
 * screen (Bundler hasn't caught up with a change yet) are ignored until the next report.
 */
/** @type {any} */
let bundlerTotals = null;
let bundlerTotalsKey = '';
/** The parts of Bundler's report the basket shows; repeats of the same report are ignored. */
const bundlerKey = (/** @type {any} */ totals) => {
  const items = totals?.discounted_cart_object?.items;
  return JSON.stringify([
    Number(totals?.discount_amount) || 0,
    Array.isArray(items) ? items.map((i) => [i.variant_id, i.quantity, i.line_price, i.original_line_price, i.handle, isFeeItem(i)]) : null,
  ]);
};
document.addEventListener('bundler:total_cart_values', (event) => {
  bundlerTotals = /** @type {CustomEvent} */ (event).detail ?? null;
  const key = bundlerKey(bundlerTotals);
  if (key === bundlerTotalsKey) return;
  bundlerTotalsKey = key;
  notify();
});

/** @param {any} item */
const isFeeItem = (item) => Boolean(item?.properties?._mws_fee) || item?.handle === 'service-fee';

/**
 * Bundler's discounted prices per variant, if they describe exactly these basket lines.
 * @param {Map<string, number>} quantities - variant id → quantity on screen
 * @returns {{ lines: Map<string, { was: number, now: number }>, saving: number } | null}
 */
function bundlerPrices(quantities) {
  const items = bundlerTotals?.discounted_cart_object?.items;
  const saving = Number(bundlerTotals?.discount_amount) || 0;
  if (!Array.isArray(items) || saving <= 0) return null;
  /** @type {Map<string, { was: number, now: number, qty: number }>} */
  const lines = new Map();
  for (const item of items) {
    if (isFeeItem(item)) continue;
    const id = String(item.variant_id);
    const line = lines.get(id) ?? { was: 0, now: 0, qty: 0 };
    line.was += Number(item.original_line_price) || 0;
    line.now += Number(item.line_price) || 0;
    line.qty += Number(item.quantity) || 0;
    lines.set(id, line);
  }
  if (lines.size !== quantities.size) return null;
  for (const [id, qty] of quantities) if (lines.get(id)?.qty !== qty) return null;
  return { lines, saving };
}

/*
 * Zapiet starts again after every cart change and falls back to its first method (Delivery), so a
 * shopper who chose Collect and then changed a quantity was quietly moved to delivery (live theme
 * too). The shopper's own choice (a tap on a method tab) is kept for the visit and put back through
 * Zapiet's public `setCheckoutMethod()` whenever its widget has loaded again.
 */
const CHOSEN_METHOD_KEY = 'cfs:zapiet-method';
let lastMethodPick = 0;
for (const type of /** @type {const} */ (['click', 'keydown'])) {
  document.addEventListener(
    type,
    (event) => {
      if (event instanceof KeyboardEvent && event.key !== 'Enter' && event.key !== ' ') return;
      if (/** @type {Element} */ (event.target)?.closest?.('#storePickupApp .checkoutMethod')) lastMethodPick = Date.now();
    },
    true
  );
}

let keepTimer = 0;
/** Puts the shopper's method back; Zapiet only accepts it once its restarted widget is ready. */
function keepChosenMethod(attempt = 0) {
  clearTimeout(keepTimer);
  try {
    const chosen = sessionStorage.getItem(CHOSEN_METHOD_KEY);
    const api = /** @type {any} */ (window).Zapiet;
    if (!chosen || !api?.setCheckoutMethod) return;
    if (readSelectedMethod() === chosen) {
      // Zapiet applies setCheckoutMethod() a moment later and fires no selected_method event.
      if (zapiet.method !== chosen) {
        zapiet.method = chosen;
        notify();
      }
      return;
    }
    if (attempt === 0 || attempt % 4 === 0) {
      if ((api.getAvailableCheckoutMethods?.() ?? []).includes(chosen)) api.setCheckoutMethod(chosen);
    }
    if (attempt < 12) keepTimer = window.setTimeout(() => keepChosenMethod(attempt + 1), 250);
  } catch {
    // Private mode or a Zapiet change: Zapiet's own default stands.
  }
}

let zapietHooked = false;
function hookZapiet() {
  if (zapietHooked) return;
  const events = window.ZapietEvent;
  if (!events?.listen) return;
  zapietHooked = true;
  events.listen('selected_method', (/** @type {any} */ method) => {
    zapiet.method = typeof method === 'string' ? method : readSelectedMethod();
    if (zapiet.method && Date.now() - lastMethodPick < 2000) {
      try {
        sessionStorage.setItem(CHOSEN_METHOD_KEY, zapiet.method);
      } catch {
        // Private mode: the choice just isn't kept.
      }
    } else {
      keepChosenMethod(); // Zapiet restarted on its default
    }
    notify();
  });
  events.listen('widget_loaded', () => {
    zapiet.rendered = true;
    keepChosenMethod();
    notify(); // label its freshly rendered method tabs
  });
  events.listen('checkoutDisabled', () => {
    zapiet.blocked = true;
    notify();
  });
  events.listen('checkoutEnabled', () => {
    zapiet.blocked = false;
    notify();
  });
}


/*
 * Delivery-method restrictions. Zapiet publishes its method rules on the page
 * (window.ZapietMethods[method].status.product_conditions: product tags that exclude a product
 * from that method, e.g. 0Shipping_Restricted). The basket only reads them to explain which line
 * blocks checkout; Zapiet still decides and enforces.
 */
/** @type {Record<string, string>} */
const METHOD_NAMES = { shipping: 'delivery', delivery: 'local delivery', pickup: 'click & collect' };
const methodName = (/** @type {string} */ m) => METHOD_NAMES[m] ?? m;

function zapietMethods() {
  const methods = /** @type {any} */ (window).ZapietMethods;
  return methods && typeof methods === 'object' ? methods : null;
}

/** @param {string} method */
function excludedTags(method) {
  const conditions = zapietMethods()?.[method]?.status?.product_conditions;
  if (!conditions?.enabled || conditions.property !== 'tags') return [];
  return String(conditions.value ?? '').split(',').map((tag) => tag.trim()).filter(Boolean);
}

function enabledMethods() {
  const methods = zapietMethods();
  if (!methods) return [];
  return Object.keys(methods)
    .filter((key) => methods[key]?.enabled)
    .sort((a, b) => (methods[a].sort ?? 0) - (methods[b].sort ?? 0));
}

/**
 * @param {number} pence
 * @param {string} format - shop.money_format, e.g. "£{{amount}}"
 */
function money(pence, format) {
  const amount = (pence / 100).toFixed(2);
  const withCommas = amount.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return (format || '£{{amount}}').replace(/\{\{\s*amount\s*\}\}/, withCommas).replace(/\{\{\s*amount_no_decimals\s*\}\}/, String(Math.round(pence / 100)));
}

/** Whole-pound threshold labels: £40, £65. */
function shortMoney(pence, format) {
  return pence % 100 === 0 ? money(pence, format).replace(/\.00\b/, '') : money(pence, format);
}

class CfsBasket extends HTMLElement {
  #applying = false;
  #frame = 0;
  /** @type {MutationObserver | undefined} */
  #observer;

  #zapietTimer = 0;

  connectedCallback() {
    hookZapiet();
    zapiet.method ??= readSelectedMethod();
    listeners.add(this.#schedule);
    document.addEventListener(CART_STATE_EVENT, this.#schedule);
    if (this.dataset.context === 'page') document.addEventListener(CART_STATE_EVENT, this.#refreshPage);
    this.addEventListener('click', this.#onClick);
    this.addEventListener('change', this.#onQtyChange);
    this.addEventListener('keydown', this.#onQtyKeydown);
    this.addEventListener('focusin', this.#onQtyFocus);

    // Re-apply after Horizon morphs a re-rendered drawer into this element.
    this.#observer = new MutationObserver(() => {
      if (!this.#applying) this.#schedule();
    });
    this.#observer.observe(this, { subtree: true, childList: true, characterData: true, attributes: true, attributeFilter: ['data-subtotal', 'data-fee', 'data-count', 'data-product-id', 'data-exclude'] });

    this.#ensureZapiet();
    this.#apply();
    this.#loadExtras();

    // Zapiet loads after the theme; pick up its method/events once it is there.
    if (!window.ZapietEvent?.listen || !zapiet.method) {
      let tries = 0;
      const wait = window.setInterval(() => {
        hookZapiet();
        const m = readSelectedMethod();
        if (m && m !== zapiet.method) {
          zapiet.method = m;
          notify();
        }
        if ((zapietHooked && zapiet.method) || ++tries > 20) window.clearInterval(wait);
      }, 500);
    }
  }

  disconnectedCallback() {
    listeners.delete(this.#schedule);
    document.removeEventListener(CART_STATE_EVENT, this.#schedule);
    document.removeEventListener(CART_STATE_EVENT, this.#refreshPage);
    this.#observer?.disconnect();
  }

  #refreshController = new AbortController();

  /** Cart page: re-render this basket from its section, keeping Zapiet's live widget. */
  #refreshPage = async (/** @type {any} */ event) => {
    if (Number(event?.detail?.item_count) === 0) {
      window.location.reload();
      return;
    }
    const sectionId = this.dataset.sectionId;
    if (!sectionId) return;
    this.#refreshController.abort();
    this.#refreshController = new AbortController();
    try {
      const url = `${window.location.pathname}?section_id=${encodeURIComponent(sectionId)}`;
      const html = await (await fetch(url, { signal: this.#refreshController.signal })).text();
      const fresh = new DOMParser().parseFromString(html, 'text/html').querySelector('cfs-basket');
      if (!fresh) return;

      const zapietNode = this.querySelector('.cfs-basket__zapiet');
      const extrasNode = this.querySelector('[data-cfs-extras]');
      zapietNode?.remove();
      morph(this, fresh);
      if (zapietNode) this.querySelector('.cfs-basket__zapiet')?.replaceWith(zapietNode);
      if (extrasNode?.firstElementChild) this.querySelector('[data-cfs-extras]')?.replaceWith(extrasNode);
      for (const name of ['subtotal', 'fee', 'count', 'method']) {
        const value = fresh.getAttribute(`data-${name}`);
        if (value !== null) setAttr(this, `data-${name}`, value);
      }
      const heading = document.querySelector('.cfs-cart-page__title .cfs-basket-heading__count');
      const count = Number(fresh.getAttribute('data-count'));
      setText(heading, `${count} ${count === 1 ? 'item' : 'items'}`);
      this.#schedule();
    } catch (error) {
      if (/** @type {Error} */ (error)?.name !== 'AbortError') console.warn('[cfs-basket] Refresh failed', error);
    }
  };

  #schedule = () => {
    cancelAnimationFrame(this.#frame);
    this.#frame = requestAnimationFrame(() => {
      this.#ensureZapiet();
      this.#apply();
      this.#loadExtras();
    });
  };

  /**
   * Zapiet starts itself on page load and rebuilds its widget after every cart request it sees,
   * so normally nothing is needed here (the drawer's mount sits outside Horizon's re-rendered
   * area). Fallback only: if the basket has items but the mount is still empty a few seconds
   * later (e.g. Zapiet loaded while the basket was empty), send `zapiet:start` once.
   */
  #ensureZapiet() {
    const scope = this.closest('cart-drawer-component') ?? this;
    // Zapiet's method tabs are images (hidden; the theme shows "Delivery" / "Collect" as CSS
    // text): give each tab a real accessible name too (a11y audit).
    for (const tab of scope.querySelectorAll('#storePickupApp .checkoutMethod')) {
      const alt = tab.querySelector('img')?.getAttribute('alt') ?? '';
      const name = alt.startsWith('shipping') ? 'Delivery' : alt.startsWith('pickup') ? 'Collect' : alt.startsWith('delivery') ? 'Local delivery' : '';
      if (name) setAttr(tab, 'aria-label', name);
    }
    let mount = scope.querySelector('#storePickupApp');
    if (!mount) {
      const holder = scope.querySelector('.cfs-drawer__zapiet, .cfs-basket__zapiet');
      if (!holder) return;
      holder.insertAdjacentHTML('beforeend', '<div id="storePickupApp"></div>');
      mount = holder.lastElementChild;
    }
    if (!mount || mount.childElementCount > 0) {
      zapiet.rendered = true;
      return;
    }
    if (Number(this.dataset.count) === 0 || this.#zapietTimer) return;
    this.#zapietTimer = window.setTimeout(() => {
      this.#zapietTimer = 0;
      const current = scope.querySelector('#storePickupApp');
      if (current && current.childElementCount === 0 && window.Zapiet && Number(this.dataset.count) > 0) {
        document.dispatchEvent(new CustomEvent('zapiet:start'));
      }
    }, 3000);
  }

  /** The "… removed · Undo" row in the removed line's place, while its undo lasts. */
  #placeUndo() {
    const list = this.querySelector('.cfs-basket__lines');
    if (!list) return;
    const undo = readUndo();
    let row = /** @type {HTMLElement | null} */ (list.querySelector('.cfs-line-undo'));
    const lines = [...list.querySelectorAll('.cfs-line[data-variant-id]')];
    const back = undo && lines.some((line) => /** @type {HTMLElement} */ (line).dataset.variantId === undo.variantId);
    if (!undo || back) {
      row?.remove();
      return;
    }
    if (!row) {
      row = document.createElement('li');
      row.className = 'cfs-line-undo';
      row.append(document.createElement('cfs-basket-undo'));
    }
    const before = lines[undo.index] ?? null;
    if (row.nextElementSibling !== before || row.parentElement !== list) list.insertBefore(row, before);
  }

  #apply() {
    this.#applying = true;
    try {
      this.#placeUndo();
      showFlash(this);
      for (const undo of this.querySelectorAll('cfs-basket-undo')) /** @type {any} */ (undo).refresh?.();
      // Line warnings survive re-renders until they expire (lineWarnings)
      for (const el of /** @type {NodeListOf<HTMLElement>} */ (this.querySelectorAll('.cfs-line[data-variant-id]'))) {
        const warning = lineWarnings.get(el.dataset.variantId ?? '');
        const live = warning && warning.until > Date.now() ? warning.text : '';
        if (warning && !live) lineWarnings.delete(el.dataset.variantId ?? '');
        setText(el.querySelector('[data-cfs-line-message]'), live);
      }
      // Typed quantities: a re-render can leave an edited field showing an old number.
      for (const input of /** @type {NodeListOf<HTMLInputElement>} */ (this.querySelectorAll('input[data-cfs-line-qty]'))) {
        const quantity = /** @type {HTMLElement | null} */ (input.closest('.cfs-line'))?.dataset.quantity;
        if (quantity && input !== document.activeElement && input.value !== quantity) input.value = quantity;
      }
      const d = this.dataset;
      const format = d.moneyFormat ?? '£{{amount}}';
      const subtotal = Number(d.subtotal) || 0;
      const fee = Number(d.fee) || 0;
      const localFree = Number(d.localFree) || 4000;
      const ukFree = Number(d.ukFree) || 6500;
      const pickupMin = Number(d.pickupMin) || 1149;
      const method = zapiet.method ?? d.method ?? 'shipping';

      const el = (/** @type {string} */ sel) => /** @type {HTMLElement | null} */ (this.querySelector(sel));
      const progress = el('[data-cfs-progress]');
      const progressText = el('[data-cfs-progress-text]');
      const bar = el('[data-cfs-progress-bar]');
      const deliveryLabel = el('[data-cfs-delivery-label]');
      const deliveryValue = el('[data-cfs-delivery-value]');
      const totalLabel = el('[data-cfs-total-label]');
      const warning = el('[data-cfs-collect-warning]');
      const postcodeRow = el('[data-cfs-basket-postcode]');
      const checkout = /** @type {HTMLButtonElement | null} */ (this.querySelector('[data-cfs-checkout]'));
      const checkoutLabel = el('[data-cfs-checkout-label]');


      // Free-delivery bars count items after Bundler's multibuy savings (owner, 5 Oct 2026: Parcel
      // should judge the discounted total). If Parcel used the full total this only errs on the
      // safe side; it never promises free delivery that checkout then charges for.
      const towardsFree = subtotal - (this.#multibuyPrices()?.saving ?? 0);

      /** @param {number} target @param {string} text @param {string} doneText */
      const setBar = (target, text, doneText) => {
        if (!progress || !progressText || !bar) return;
        setHidden(progress, false);
        const pct = Math.min(100, Math.round((towardsFree / target) * 100));
        setText(progressText, towardsFree >= target ? doneText : text.replace('{amount}', money(target - towardsFree, format)));
        setAttr(bar, 'aria-valuenow', String(pct));
        const fill = /** @type {HTMLElement} */ (bar.firstElementChild);
        if (fill.style.width !== `${pct}%`) fill.style.width = `${pct}%`;
        progress.classList.toggle('is-complete', towardsFree >= target);
      };

      let blocked = zapiet.blocked;
      let warningText = '';
      let label = 'Checkout';

      if (method === 'pickup') {
        setHidden(postcodeRow, true);
        setHidden(progress, true);
        setText(deliveryLabel, 'Click & collect');
        setText(deliveryValue, 'Free');
        setText(totalLabel, 'Total');
        const short = pickupMin - (subtotal + fee);
        if (short > 0) {
          blocked = true;
          warningText = `Collection needs a minimum of ${money(pickupMin, format)} including the service fee. Add ${money(short, format)} more, or choose delivery.`;
          label = `Add ${money(short, format)} more to collect`;
        }
      } else {
        // Zapiet "shipping" = UK delivery (Parcel, free over £65); "delivery" = local delivery
        // (Zapiet by distance, free over £40). Rates are worked out at checkout from the address.
        setHidden(postcodeRow, true);
        const isLocalMethod = method === 'delivery';
        if (isLocalMethod) {
          setBar(localFree, `{amount} away from free local delivery (${shortMoney(localFree, format)})`, "You've got free local delivery");
        } else {
          setBar(ukFree, `{amount} away from free UK delivery (${shortMoney(ukFree, format)}, mainland)`, "You've got free UK delivery (mainland)");
        }
        setText(deliveryLabel, isLocalMethod ? 'Local delivery' : 'Delivery');
        // The bar shows the free-delivery target; the price itself comes from Parcel/Zapiet at checkout
        setText(deliveryValue, 'Calculated at checkout');
        setText(totalLabel, 'Total before delivery');
      }

      // Total (with Bundler's multibuy price when there is one).
      this.#showMultibuy(subtotal + fee, format);

      // Lines Zapiet won't allow for this method: flag them and say how to fix it.
      const restricted = this.#markRestrictions(method);
      if (restricted.length) {
        blocked = true;
        const names = restricted.map((r) => r.title);
        const list = names.length === 1 ? names[0] : `${names.slice(0, -1).join(', ')} and ${names.at(-1)}`;
        const alternatives = [...new Set(restricted.flatMap((r) => r.allowed))].filter((m) => restricted.every((r) => r.allowed.includes(m)));
        warningText =
          `${list} ${names.length === 1 ? "isn't" : "aren't"} available for ${methodName(method)}. Remove ${names.length === 1 ? 'it' : 'them'}` +
          (alternatives.length ? `, or switch to ${alternatives.map(methodName).join(' or ')}.` : ' to check out.');
        label = `Remove ${names.length} ${names.length === 1 ? 'item' : 'items'} to check out`;
      }

      setHidden(warning, !warningText);
      setText(warning, warningText);
      if (checkout) {
        if (checkout.disabled !== blocked) checkout.disabled = blocked;
        checkout.toggleAttribute('aria-disabled', blocked);
        if (blocked && !warningText && method === 'pickup') label = 'Choose a collection time';
      }
      setText(checkoutLabel, label);
    } finally {
      // Let the MutationObserver records from our own writes pass first.
      queueMicrotask(() => (this.#applying = false));
    }
  }

  /** Bundler's prices for the lines on screen, or null (no multibuy, or Bundler not caught up). */
  #multibuyPrices() {
    /** @type {Map<string, number>} */
    const quantities = new Map();
    for (const row of /** @type {NodeListOf<HTMLElement>} */ (this.querySelectorAll('.cfs-line[data-variant-id]'))) {
      const id = String(row.dataset.variantId);
      quantities.set(id, (quantities.get(id) ?? 0) + (Number(row.dataset.quantity) || 0));
    }
    return bundlerPrices(quantities);
  }

  /**
   * Bundler's multibuy prices: struck-through line totals, a "Multibuy savings" row and the
   * total after savings. Re-applied after every re-render (morphs bring back the plain prices).
   * @param {number} total - items + fee, before savings
   * @param {string} format
   */
  #showMultibuy(total, format) {
    const rows = /** @type {HTMLElement[]} */ ([...this.querySelectorAll('.cfs-line[data-variant-id]')]);
    const prices = this.#multibuyPrices();

    for (const row of rows) {
      const cell = /** @type {HTMLElement | null} */ (row.querySelector('.cfs-line__total'));
      if (!cell) continue;
      cell.dataset.cfsPrice ??= cell.textContent ?? '';
      const line = prices?.lines.get(String(row.dataset.variantId));
      if (line && line.now < line.was) {
        setHtml(cell, `<s class="cfs-line__was"><span class="visually-hidden">was </span>${money(line.was, format)}</s> <span class="cfs-line__now">${money(line.now, format)}</span>`);
        row.classList.toggle('cfs-line--multibuy', true);
      } else if (row.classList.contains('cfs-line--multibuy')) {
        setText(cell, cell.dataset.cfsPrice ?? '');
        row.classList.remove('cfs-line--multibuy');
      }
    }

    const savingRow = /** @type {HTMLElement | null} */ (this.querySelector('[data-cfs-multibuy-row]'));
    const savingValue = this.querySelector('[data-cfs-multibuy-value]');
    setHidden(savingRow, !prices);
    if (prices) setText(savingValue, `−${money(prices.saving, format)}`);

    const totalValue = /** @type {HTMLElement | null} */ (this.querySelector('[data-cfs-total-value]'));
    if (prices) {
      setHtml(totalValue, `<s class="cfs-basket__was"><span class="visually-hidden">was </span>${money(total, format)}</s> ${money(total - prices.saving, format)}`);
    } else {
      setText(totalValue, money(total, format));
    }
  }

  /**
   * Marks basket lines whose product tags exclude them from the chosen method.
   * @param {string} method
   * @returns {{ title: string, allowed: string[] }[]}
   */
  #markRestrictions(method) {
    const methods = enabledMethods();
    /** @type {{ title: string, allowed: string[] }[]} */
    const restricted = [];
    for (const line of this.querySelectorAll('.cfs-line[data-product-tags]')) {
      const el = /** @type {HTMLElement} */ (line);
      const tags = (el.dataset.productTags ?? '').split('|');
      const blockedFor = (/** @type {string} */ m) => excludedTags(m).some((tag) => tags.includes(tag));
      const isBlocked = methods.length > 0 && blockedFor(method);
      const note = /** @type {HTMLElement | null} */ (el.querySelector('[data-cfs-line-restriction]'));
      el.classList.toggle('cfs-line--blocked', isBlocked);
      if (isBlocked) {
        const allowed = methods.filter((m) => m !== method && !blockedFor(m));
        const title = el.querySelector('.cfs-line__title')?.textContent?.trim() ?? 'This item';
        restricted.push({ title, allowed });
        setText(
          note,
          allowed.length
            ? `Not available for ${methodName(method)} · ${methodName(allowed[0]).replace(/^./, (c) => c.toUpperCase())}${allowed.length > 1 ? ` or ${allowed.slice(1).map(methodName).join(' or ')}` : ''} only`
            : `Not available for ${methodName(method)}`
        );
      }
      setHidden(note, !isBlocked);
    }
    return restricted;
  }

  /** @param {MouseEvent} event */
  #onClick = async (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    const changeButton = target.closest('[data-cfs-line-change], [data-cfs-line-remove]');
    if (!changeButton) return;
    const line = /** @type {HTMLElement | null} */ (changeButton.closest('.cfs-line'));
    if (!line || line.hasAttribute('aria-busy')) return;

    const current = Number(line.dataset.quantity) || 0;
    const next = changeButton.hasAttribute('data-cfs-line-remove')
      ? 0
      : Math.max(0, current + Number(/** @type {HTMLElement} */ (changeButton).dataset.cfsLineChange));
    await this.#setLineQuantity(line, current, next);
  };

  /** Typed quantity (owner): applied on Enter or when the field loses focus. @param {Event} event */
  #onQtyChange = async (event) => {
    const input = /** @type {HTMLInputElement} */ (event.target);
    if (!input.matches?.('[data-cfs-line-qty]')) return;
    const line = /** @type {HTMLElement | null} */ (input.closest('.cfs-line'));
    if (!line) return;
    const current = Number(line.dataset.quantity) || 0;
    const typed = Math.floor(Number(input.value));
    if (input.value.trim() === '' || !Number.isFinite(typed) || typed < 0) {
      input.value = String(current); // nothing sensible typed: back to the basket's quantity
      return;
    }
    const next = Math.min(999, typed);
    if (next === current) {
      input.value = String(current);
      return;
    }
    if (line.hasAttribute('aria-busy')) {
      input.value = String(current);
      return;
    }
    await this.#setLineQuantity(line, current, next);
  };

  /** Enter applies the typed quantity (and closes the phone keyboard). @param {KeyboardEvent} event */
  #onQtyKeydown = (event) => {
    const input = /** @type {HTMLElement} */ (event.target);
    if (event.key === 'Enter' && input.matches?.('[data-cfs-line-qty]')) {
      event.preventDefault();
      input.blur();
    }
  };

  /** Selects the number on focus, so typing replaces it. @param {FocusEvent} event */
  #onQtyFocus = (event) => {
    const input = /** @type {HTMLInputElement} */ (event.target);
    if (!input.matches?.('[data-cfs-line-qty]')) return;
    input.select();
    requestAnimationFrame(() => input.select()); // again after a tap's own caret placement
  };

  /**
   * @param {HTMLElement} line
   * @param {number} current
   * @param {number} next
   */
  async #setLineQuantity(line, current, next) {
    const qtyEl = /** @type {HTMLInputElement | null} */ (line.querySelector('[data-cfs-line-qty]'));
    const message = /** @type {HTMLElement | null} */ (line.querySelector('[data-cfs-line-message]'));

    line.setAttribute('aria-busy', 'true');
    if (qtyEl) qtyEl.value = String(next);
    if (message) message.textContent = '';
    const variantId = line.dataset.variantId ?? '';
    lineWarnings.delete(variantId);
    const title = line.querySelector('.cfs-line__title')?.textContent?.trim() ?? 'Item';
    const index = [...this.querySelectorAll('.cfs-line[data-variant-id]')].indexOf(line);
    try {
      if (next === 0) setUndo({ variantId, quantity: current, title, index }); // shown as soon as the line goes
      await setVariantQuantity({ variantId, quantity: next, source: this });
      if (next !== 0 && readUndo()?.variantId === variantId) setUndo(null);
    } catch (error) {
      if (next === 0) setUndo(null);
      if (qtyEl) qtyEl.value = String(current);
      const text = /** @type {Error} */ (error)?.message || 'Could not update your basket';
      lineWarnings.set(variantId, { text, until: Date.now() + LINE_WARNING_MS });
      if (message) message.textContent = text;
      window.setTimeout(notify, LINE_WARNING_MS + 50); // clears it
    } finally {
      line.removeAttribute('aria-busy');
    }
  }

  /** "Little extras": load once per first product, hide what is already in the basket. */
  async #loadExtras() {
    const holder = /** @type {HTMLElement | null} */ (this.querySelector('[data-cfs-extras]'));
    const meta = /** @type {HTMLElement | null} */ (this.querySelector('[data-cfs-extras-meta]'));
    if (!holder || !meta) return;
    const productId = meta.dataset.productId;
    if (!productId) {
      if (holder.firstChild) holder.replaceChildren();
      return;
    }
    if (holder.dataset.loadedFor !== productId || !holder.firstElementChild) {
      holder.dataset.loadedFor = productId;
      const content = await loadExtras(productId);
      if (holder.dataset.loadedFor !== productId) return;
      holder.replaceChildren(...(content ? [document.importNode(content, true)] : []));
    }
    const exclude = meta.dataset.exclude ?? '';
    let shown = 0;
    for (const item of holder.querySelectorAll('.cfs-extras__item')) {
      const inBasket = exclude.includes(`,${/** @type {HTMLElement} */ (item).dataset.variantId},`);
      const show = !inBasket && shown < 3;
      setHidden(/** @type {HTMLElement} */ (item), !show);
      if (show) shown++;
    }
    setHidden(/** @type {HTMLElement | null} */ (holder.querySelector('.cfs-extras__title')), shown === 0);
  }
}

/** @type {Map<string, Promise<Element | null>>} */
const extrasCache = new Map();
/** @param {string} productId */
function loadExtras(productId) {
  if (!extrasCache.has(productId)) {
    const root = window.Shopify?.routes?.root ?? '/';
    const url = `${root}recommendations/products?product_id=${encodeURIComponent(productId)}&intent=related&limit=10&section_id=cfs-basket-extras`;
    extrasCache.set(
      productId,
      fetch(url)
        .then((r) => (r.ok ? r.text() : ''))
        .then((html) => new DOMParser().parseFromString(html, 'text/html').querySelector('[data-cfs-extras-content]'))
        .catch(() => null)
    );
  }
  return /** @type {Promise<Element | null>} */ (extrasCache.get(productId));
}

if (!customElements.get('cfs-basket')) customElements.define('cfs-basket', CfsBasket);
